import React, { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import dayjs from '~/timeInit';

import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';

import { useListCampaignEnrollments } from '~/hooks/apis/loyalty/useListCampaignEnrollments';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';

import type { CampaignEnrollmentState, ClaimCampaign } from '~/services/LuxLoyalty';

import CampaignCustomerSearch from './Claims/CampaignCustomerSearch';
import {
  ENROLLMENT_STATE_DISPLAY,
  getEnrollmentStatusDisplay,
  humaniseCheckKey,
  tracksCheckProgress,
} from './Claims/enrollmentProgress';
import { formatMatchCheckUntil } from './Claims/matchCheckUntil';

const STATE_TABS: Array<CampaignEnrollmentState | 'ALL'> = ['ALL', 'ACTIVE', 'FULFILLED', 'EXPIRED', 'CANCELLED'];

const PAGE_SIZE = 25;

interface CampaignEnrollmentsDialogProps {
  open: boolean;
  campaign: ClaimCampaign | null;
  onClose: () => void;
}

/** Same date format as the campaign form */
function formatDate(value: string): string {
  return dayjs(value).format('DD/MM/YYYY');
}

/**
 * Who is enrolled in a MATCH campaign (Status Match or Status Challenge) and where they're at:
 * enrolled, completed (upgraded), expired, or removed. Searchable by customer.
 */
export default function CampaignEnrollmentsDialog({ open, campaign, onClose }: CampaignEnrollmentsDialogProps) {
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const [stateTab, setStateTab] = useState<CampaignEnrollmentState | 'ALL'>('ALL');
  const [page, setPage] = useState(0);
  const [customerId, setCustomerId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStateTab('ALL');
      setPage(0);
      setCustomerId(null);
    }
  }, [open]);

  const stateFilter = stateTab === 'ALL' ? undefined : stateTab;
  const { data, isLoading, error } = useListCampaignEnrollments(
    open ? (campaign?.id ?? null) : null,
    stateFilter,
    page,
    PAGE_SIZE,
    customerId ?? undefined,
  );

  // Name / email for the customers on this page, so admins can recognise them without opening each one
  const customerIds = data?.enrollments.map((enrollment) => enrollment.customerId) ?? [];
  const { data: summaries } = useGetUsersSummaryByIds({ userIds: customerIds, brand });

  // Status Challenges: show each member's check progress and "On track", and call expiresAt what it is (book-by)
  const showProgress = !!campaign && tracksCheckProgress(campaign);

  const summary = data?.summary;
  // The counts are for the whole campaign, so leave them off while the list is narrowed to one customer
  const tabCount = (tab: CampaignEnrollmentState | 'ALL') => {
    if (!summary || customerId) {
      return undefined;
    }
    return tab === 'ALL' ? Object.values(summary).reduce((sum, count) => sum + count, 0) : summary[tab];
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Enrolments
        {campaign && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            — {campaign.name}
            {showProgress &&
              campaign.matchCheckUntil &&
              ` · Checking until ${formatMatchCheckUntil(campaign.matchCheckUntil)}`}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={stateTab}
            onChange={(_, value: CampaignEnrollmentState | 'ALL') => {
              setStateTab(value);
              setPage(0);
            }}
          >
            {STATE_TABS.map((tab) => {
              const count = tabCount(tab);
              const label = tab === 'ALL' ? 'All' : ENROLLMENT_STATE_DISPLAY[tab].label;
              return (
                <Tab
                  key={tab}
                  value={tab}
                  label={count === undefined ? label : `${label} (${count.toLocaleString()})`}
                />
              );
            })}
          </Tabs>
        </Box>

        <Box p={2}>
          <Box mb={2}>
            <CampaignCustomerSearch
              id="campaign-enrollments-customer-search"
              onCustomerChange={(id) => {
                setCustomerId(id);
                setPage(0);
              }}
            />
          </Box>

          {isLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {error && <Alert severity="error">Failed to load enrolments: {error.message}</Alert>}

          {!isLoading && !error && data && (
            <>
              {data.enrollments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" py={2}>
                  {customerId ? 'This customer is not enrolled in this campaign.' : 'No enrolments.'}
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer ID</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Status</TableCell>
                      {showProgress && <TableCell>Checks</TableCell>}
                      <TableCell>Enrolled</TableCell>
                      <TableCell>{showProgress ? 'Book by' : 'Expires'}</TableCell>
                      <TableCell>Target tier</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.enrollments.map((enrollment) => {
                      const user = summaries?.get(enrollment.customerId);
                      const { label, color } = campaign
                        ? getEnrollmentStatusDisplay(enrollment, campaign)
                        : ENROLLMENT_STATE_DISPLAY[enrollment.state];
                      return (
                        <TableRow key={enrollment.id}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {enrollment.customerId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{user?.full_name || '—'}</Typography>
                            {user?.email && (
                              <Typography variant="caption" color="text.secondary">
                                {user.email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={label} color={color} size="small" />
                          </TableCell>
                          {showProgress && (
                            <TableCell>
                              {enrollment.checks?.length ? (
                                <Stack direction="row" spacing={0.5}>
                                  {enrollment.checks.map((check) => (
                                    <Chip
                                      key={check.key}
                                      size="small"
                                      variant="outlined"
                                      color={check.eligible ? 'success' : 'default'}
                                      icon={check.eligible ? <CheckIcon /> : <CloseIcon />}
                                      label={humaniseCheckKey(check.key)}
                                      aria-label={`${humaniseCheckKey(check.key)}: ${check.eligible ? 'passed' : 'not passed'}`}
                                    />
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(enrollment.enrolledAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(enrollment.expiresAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {enrollment.targetTier ?? '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              <TablePagination
                component="div"
                count={data.pagination.total}
                page={page}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                onPageChange={(_, newPage) => setPage(newPage)}
              />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
